
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Policy } from "./types";
import { usePolicyForm } from "@/hooks/usePolicyForm";
import { PolicyFormContainer } from "./form/components/PolicyFormContainer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LeadSourceType } from "@/types/leadSource";
import { useEffect, useState } from "react";

interface PolicyEditDialogProps {
  policy: Policy | null;
  onClose: () => void;
  onSave: () => void;
}

export const PolicyEditDialog = ({ policy, onClose, onSave }: PolicyEditDialogProps) => {
  const [loaded, setLoaded] = useState(false);
  
  const {
    producer,
    policyType,
    insuranceCarrier,
    policyLine,
    policyNumber,
    startDate,
    endDate,
    premiumAmount,
    status,
    leadSource,
    policyAcquisitionType,
    setProducer,
    setInsuranceCarrier,
    setPolicyLine,
    setPolicyNumber,
    setPremiumAmount,
    setStatus,
    setStartDate,
    setEndDate,
    setLeadSource,
    setPolicyAcquisitionType,
    handlePolicyTypeChange,
    handleSubmit,
    isFormValid
  } = usePolicyForm({
    policy,
    onSuccess: onSave
  });

  // Ensure form fields are updated when policy changes
  useEffect(() => {
    if (policy) {
      console.log("PolicyEditDialog: Policy loaded:", policy);
      
      // Reset the loaded state to trigger a re-render
      setLoaded(false);
      
      // Set all form fields with policy data
      setProducer(policy.producer_id || "");
      setPolicyLine(policy.policy_line || "");
      handlePolicyTypeChange(policy.insurance_type || "");
      setInsuranceCarrier(policy.insurance_carrier || "");
      setPolicyNumber(policy.policy_number || "");
      setPremiumAmount(policy.premium_amount || 0);
      setStatus(policy.status || "active");
      
      // Explicitly cast lead_source to LeadSourceType
      if (policy.lead_source) {
        const leadSourceValue = policy.lead_source as LeadSourceType;
        console.log("Setting lead source to:", leadSourceValue);
        setLeadSource(leadSourceValue);
      } else {
        console.log("No lead source found in policy, using default");
        setLeadSource("Corporate Website");
      }
      
      setPolicyAcquisitionType(policy.policy_acquisition_type || "New Business");
      
      if (policy.start_date) {
        setStartDate(new Date(policy.start_date));
      }
      
      if (policy.end_date) {
        setEndDate(new Date(policy.end_date));
      }
      
      // Mark as loaded after setting all values
      setTimeout(() => setLoaded(true), 100);
    }
  }, [policy]);

  console.log("PolicyEditDialog rendering with lead source:", leadSource);

  return (
    <Dialog open={!!policy} onOpenChange={() => policy && onClose()}>
      <DialogContent className="sm:max-w-[600px] px-6">
        <DialogHeader>
          <DialogTitle>Edit Policy</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[60vh] px-2">
          <div className="space-y-4 py-4">
            {loaded && (
              <PolicyFormContainer
                producer={producer}
                policyType={policyType}
                insuranceCarrier={insuranceCarrier}
                policyLine={policyLine}
                policyNumber={policyNumber}
                startDate={startDate}
                endDate={endDate}
                premiumAmount={premiumAmount}
                status={status}
                leadSource={leadSource}
                policyAcquisitionType={policyAcquisitionType}
                onProducerChange={setProducer}
                onPolicyTypeChange={handlePolicyTypeChange}
                onInsuranceCarrierChange={setInsuranceCarrier}
                onPolicyLineChange={setPolicyLine}
                onPolicyNumberChange={(e) => setPolicyNumber(e.target.value)}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onPremiumAmountChange={setPremiumAmount}
                onStatusChange={setStatus}
                onLeadSourceChange={(value: LeadSourceType) => {
                  console.log("PolicyEditDialog: Lead source changed to:", value);
                  setLeadSource(value);
                }}
                onPolicyAcquisitionTypeChange={setPolicyAcquisitionType}
                onSubmit={handleSubmit}
                isSubmitDisabled={!isFormValid}
                onCancel={onClose}
                submitLabel="Save Changes"
              />
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
